import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip as RechartsTooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import { Target, Users, BookOpen, AlertTriangle, Lightbulb, Activity, ChevronRight, Briefcase, GraduationCap, ChevronDown, Search, Award, Star, TrendingUp, CheckCircle } from 'lucide-react';
import api from '../api';



const getBatchLabel = (student) => {
  const profile = student.student_profile || student;
  const parts = [];
  if (profile.graduation_level) parts.push(profile.graduation_level.trim());
  if (profile.program) parts.push(profile.program.trim());
  if (profile.branch) parts.push(profile.branch.trim());

  const base = parts.join(' - ');
  const yearPart = profile.academic_batch || profile.graduation_year || '';

  if (base && yearPart) return `${base} (${yearPart})`;
  if (base) return base;
  if (yearPart) return yearPart;
  return "Unknown Batch";
};

const BatchSkillAnalytics = ({ students = [], jobs = [] }) => {
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isBatchDropdownOpen, setIsBatchDropdownOpen] = useState(false);
  const [jobSearchText, setJobSearchText] = useState('');
  const batchDropdownRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (batchDropdownRef.current && !batchDropdownRef.current.contains(event.target)) {
        setIsBatchDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [marketRequirements, setMarketRequirements] = useState([]);
  const [analyzingJD, setAnalyzingJD] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  // Job roles posted by the TPO under Campus Jobs. They are merged into the role
  // dropdown alongside the Mantrika-assigned roles passed in via `jobs`.
  const [campusJobs, setCampusJobs] = useState([]);
  const [campusJobsLoaded, setCampusJobsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchCampusJobs = async () => {
      try {
        const res = await api.get('/tpo/internal-jobs');
        if (!cancelled) setCampusJobs(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        // Campus Jobs may be disabled/unavailable for this institution — fall back
        // to the Mantrika-assigned roles only instead of breaking the dropdown.
        console.error('Failed to load campus job roles', err);
        if (!cancelled) setCampusJobs([]);
      } finally {
        if (!cancelled) setCampusJobsLoaded(true);
      }
    };
    fetchCampusJobs();
    return () => { cancelled = true; };
  }, []);

  // Normalised role list used by the dropdown: Mantrika-assigned roles plus the
  // TPO's Campus Jobs. `selectionKey` namespaces ids so the two sources cannot collide.
  const availableRoles = useMemo(() => {
    const mantrikaRoles = (jobs || []).map(job => ({
      ...job,
      job_name: job.job_name || job.title || 'Untitled Role',
      selectionKey: `mantrika-${job.id}`,
      roleSource: 'mantrika'
    }));
    const campusRoles = (campusJobs || []).map(job => ({
      ...job,
      job_name: job.title || job.job_name || 'Untitled Role',
      must_have_skills: job.must_have_skills || [],
      good_to_have_skills: job.good_to_have_skills || [],
      selectionKey: `campus-${job.id}`,
      roleSource: 'campus'
    }));
    return [...mantrikaRoles, ...campusRoles];
  }, [jobs, campusJobs]);

  // Flattens the must-have / good-to-have skill lists of a set of roles into plain
  // skill names ready to be sent to the AI aggregation endpoint.
  const collectRoleSkills = (roles) => {
    let mustHave = [];
    let goodToHave = [];

    (roles || []).forEach(job => {
      if (job && Array.isArray(job.must_have_skills)) mustHave = mustHave.concat(job.must_have_skills);
      if (job && Array.isArray(job.good_to_have_skills)) goodToHave = goodToHave.concat(job.good_to_have_skills);
    });

    const toNames = (list) => list
      .map(s => (typeof s === 'object' && s !== null ? s.skill : s))
      .filter(s => s !== null && s !== undefined)
      .map(s => String(s).trim())
      .filter(Boolean);

    return { mustHave: toNames(mustHave), goodToHave: toNames(goodToHave) };
  };

  const availableBatches = useMemo(() => {
    const batches = new Set();
    students.forEach(s => {
      const label = getBatchLabel(s);
      if (label !== "Unknown Batch") {
        batches.add(label);
      }
    });
    return Array.from(batches).sort();
  }, [students]);

  // Signature of the aggregation currently shown (or being fetched), used so the
  // automatic "General Market Demand" load never repeats for the same skill set.
  const appliedMarketKeyRef = useRef(null);

  const fetchMarketRequirements = async () => {
    // "General Market Demand" (nothing selected) aggregates every role available to
    // the TPO — Mantrika-assigned roles plus Campus Jobs — so the panel still has
    // market requirements to compare the batches against.
    const targetJobs = selectedJobs.length === 0
      ? availableRoles
      : availableRoles.filter(j => selectedJobs.includes(j.selectionKey));

    const { mustHave: must_have_strings, goodToHave: good_to_have_strings } = collectRoleSkills(targetJobs);

    if (must_have_strings.length === 0 && good_to_have_strings.length === 0) {
      setMarketRequirements([]);
      return;
    }

    appliedMarketKeyRef.current = JSON.stringify([must_have_strings, good_to_have_strings]);

    setAnalyzingJD(true);
    setAnalysisError(null);
    try {
      const response = await api.post('/ai-agents/analyze-aggregated-skills', {
        must_have_skills: must_have_strings,
        good_to_have_skills: good_to_have_strings
      });
      if (response.data && response.data.skills) {
        setMarketRequirements(response.data.skills);
      } else {
        setMarketRequirements([]);
      }
    } catch (err) {
      console.error("Failed to analyze skills", err);
      if (err.response && err.response.data && err.response.data.detail) {
        setAnalysisError(err.response.data.detail);
      } else {
        setAnalysisError("Failed to have AI analyze the aggregated skills.");
      }
      setMarketRequirements([]);
    } finally {
      setAnalyzingJD(false);
    }
  };

  // Signature of the aggregated market demand for the "General Market Demand" view.
  // Null whenever explicit roles are selected (those are analysed on demand) or when
  // none of the available roles carry skills to aggregate.
  const generalMarketKey = useMemo(() => {
    if (selectedJobs.length > 0) return null;
    const { mustHave, goodToHave } = collectRoleSkills(availableRoles);
    if (mustHave.length === 0 && goodToHave.length === 0) return null;
    return JSON.stringify([mustHave, goodToHave]);
  }, [availableRoles, selectedJobs.length]);

  useEffect(() => {
    // General Market Demand loads its analytics automatically (once per distinct skill
    // set) so selecting it shows data instead of the empty state. Explicit role
    // selections still require the "Analyze with AI" button.
    if (!campusJobsLoaded) return;
    if (selectedJobs.length > 0) return;
    if (!generalMarketKey) return;
    if (analyzingJD) return;
    if (appliedMarketKeyRef.current === generalMarketKey) return;

    fetchMarketRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusJobsLoaded, generalMarketKey, selectedJobs.length, analyzingJD]);

  const parseScore = (val) => {
    if (!val) return 0;
    let num = 0;
    if (typeof val === 'number') num = val;
    else if (typeof val === 'string') num = parseFloat(val.replace(/[^0-9.]/g, ''));
    if (num > 10) return num / 10;
    return num;
  };

  const studentAverages = useMemo(() => {
    let filteredStudents = students;
    if (selectedBatches.length > 0) {
      filteredStudents = students.filter(s => selectedBatches.includes(getBatchLabel(s)));
    }

    if (filteredStudents.length === 0) return {};

    const skillSums = {};
    const skillCounts = {};

    filteredStudents.forEach(student => {
      let skillsList = [];
      const profile = student.student_profile || student;

      if (Array.isArray(profile.ai_skillset)) skillsList = skillsList.concat(profile.ai_skillset);
      if (Array.isArray(profile.ai_toolset)) skillsList = skillsList.concat(profile.ai_toolset);

      skillsList.forEach(skillObj => {
        if (typeof skillObj === 'object' && skillObj.name) {
          const name = skillObj.name;
          const score = parseScore(skillObj.score || skillObj.level || skillObj.A);
          if (score > 0) {
            skillSums[name] = (skillSums[name] || 0) + score;
            skillCounts[name] = (skillCounts[name] || 0) + 1;
          }
        }
      });
    });

    const averages = {};
    for (const skill in skillSums) {
      averages[skill] = (skillSums[skill] / skillCounts[skill]).toFixed(1);
    }
    return averages;
  }, [students, selectedBatches]);

  const studentReadiness = useMemo(() => {
    let filteredStudents = students;
    if (selectedBatches.length > 0) {
      filteredStudents = students.filter(s => selectedBatches.includes(getBatchLabel(s)));
    }

    if (filteredStudents.length === 0 || marketRequirements.length === 0) return { distribution: [], topCandidates: [] };

    let highlyReady = 0;
    let moderatelyReady = 0;
    let needsTraining = 0;
    let candidateScores = [];

    const totalMarket = marketRequirements.reduce((sum, req) => sum + (req.score || 0), 0);

    filteredStudents.forEach(student => {
      const profile = student.student_profile || student;
      let skillsList = [];
      if (Array.isArray(profile.ai_skillset)) skillsList = skillsList.concat(profile.ai_skillset);
      if (Array.isArray(profile.ai_toolset)) skillsList = skillsList.concat(profile.ai_toolset);

      let totalStudentScore = 0;

      marketRequirements.forEach(req => {
        const studentSkill = skillsList.find(s => s.name && s.name.toLowerCase() === req.name.toLowerCase());
        const rawScore = studentSkill ? parseScore(studentSkill.score || studentSkill.level || studentSkill.A) : 0;
        totalStudentScore += Math.min(rawScore, req.score || 10);
      });

      const employabilityScore = totalMarket > 0 ? Math.round((totalStudentScore / totalMarket) * 100) : 0;

      if (employabilityScore >= 80) highlyReady++;
      else if (employabilityScore >= 50) moderatelyReady++;
      else needsTraining++;

      const name = profile.full_name || profile.name || student.name || (student.user && (student.user.name || student.user.full_name)) || "Student";
      candidateScores.push({
        id: student.id || profile.id || Math.random(),
        name,
        branch: profile.branch || "General",
        score: employabilityScore
      });
    });

    candidateScores.sort((a, b) => b.score - a.score);

    return {
      distribution: [
        { name: 'Highly Ready', count: highlyReady, fill: '#10b981' },
        { name: 'Moderately Ready', count: moderatelyReady, fill: '#f59e0b' },
        { name: 'Needs Training', count: needsTraining, fill: '#ef4444' }
      ],
      topCandidates: candidateScores.slice(0, 5)
    };
  }, [students, selectedBatches, marketRequirements]);

  const sortedJobs = useMemo(() => {
    return [...availableRoles].sort((a, b) => a.job_name.localeCompare(b.job_name));
  }, [availableRoles]);

  const chartData = useMemo(() => {
    if (marketRequirements.length === 0) return [];
    return marketRequirements.map(req => {
      const studentScore = parseFloat(studentAverages[req.name] || 0);
      return {
        subject: req.name,
        StudentAverage: studentScore,
        MarketRequirement: req.score || 0,
        fullMark: 10
      };
    });
  }, [marketRequirements, studentAverages]);

  const insights = useMemo(() => {
    if (chartData.length === 0) return null;

    let totalStudent = 0;
    let totalMarket = 0;
    let strongestAreas = [];
    let gaps = [];

    chartData.forEach(item => {
      totalStudent += Math.min(item.StudentAverage, item.MarketRequirement);
      totalMarket += item.MarketRequirement;

      const gap = item.MarketRequirement - item.StudentAverage;
      if (item.StudentAverage >= item.MarketRequirement - 1) {
        strongestAreas.push({ ...item, difference: gap });
      }
      if (gap > 2) {
        gaps.push({ ...item, difference: gap });
      }
    });

    const employabilityScore = totalMarket > 0 ? Math.round((totalStudent / totalMarket) * 100) : 0;

    strongestAreas.sort((a, b) => a.difference - b.difference);
    gaps.sort((a, b) => b.difference - a.difference);

    const recommendations = [];
    if (gaps.length > 0) {
      gaps.slice(0, 2).forEach(gap => {
        recommendations.push(`Focus on upskilling students in ${gap.subject} by conducting specialized training or workshops to meet market expectations.`);
      });
    }
    if (gaps.length > 2) {
      recommendations.push(`Consider revising curriculum modules to place a stronger emphasis on ${gaps.slice(2).map(g => g.subject).join(', ')}.`);
    }
    if (gaps.length === 0) {
      recommendations.push("Batch is highly aligned with market requirements. Focus on mock interviews and soft skills.");
    }

    return {
      employabilityScore,
      strongestAreas: strongestAreas.slice(0, 4),
      gaps: gaps.slice(0, 4),
      recommendations
    };
  }, [chartData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e2e8f0', padding: '12px', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
          <p style={{ margin: '0 0 8px', fontWeight: 'bold', color: '#1e293b' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0', color: entry.color, fontSize: '13px', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span>{entry.name}:</span>
              <span style={{ fontWeight: 'bold' }}>{entry.value} / 10</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const selectAllBatches = () => {
    setSelectedBatches([]);
    setIsBatchDropdownOpen(false);
  };

  const CustomAxisTick = ({ payload, x, y, cx, cy, ...rest }) => {
    const dataItem = chartData.find(d => d.subject === payload.value);
    const isGap = dataItem && (dataItem.MarketRequirement - dataItem.StudentAverage > 1);
    const statusText = isGap ? 'Gap' : 'On Track';
    const statusColor = isGap ? '#ef4444' : '#22c55e';

    return (
      <text {...rest} x={x} y={y} cx={cx} cy={cy}>
        <tspan x={x} dy="0" fill="#475569" fontSize="11" fontWeight="600">{payload.value}</tspan>
        <tspan x={x} dy="14" fill={statusColor} fontSize="10" fontWeight="500">{statusText}</tspan>
      </text>
    );
  };



  return (
    <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid var(--faculty-border)', padding: '32px', marginTop: '32px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px', marginBottom: '32px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.35rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '800' }}>
            <div style={{ background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)', padding: '8px', borderRadius: '10px', color: '#fff' }}>
              <Activity size={20} strokeWidth={2.5} />
            </div>
            Batch Skill Proficiency vs Market Demand
          </h2>
          <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '0.95rem' }}>
            Analyze aggregate student skills, view readiness distributions, and identify top candidates for placement drives.
          </p>
        </div>

        {insights && (
          <div style={{ background: 'linear-gradient(145deg, #f0fdf4, #dcfce7)', padding: '12px 20px', borderRadius: '16px', border: '1px solid rgba(187, 247, 208, 0.6)', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.05)' }}>
            <Target size={24} color="#16a34a" />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ color: '#166534', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Expected Average Employability</span>
              <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#14532d', lineHeight: '1.1' }}>{insights.employabilityScore}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap', background: 'linear-gradient(145deg, #f8fafc, #f1f5f9)', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(226, 232, 240, 0.8)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' }}>

        <div style={{ flex: '1', minWidth: '350px', position: 'relative', zIndex: isBatchDropdownOpen ? 30 : 'auto', display: 'flex', alignItems: 'center', gap: '12px' }} ref={batchDropdownRef}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
            <GraduationCap size={15} color="#64748b" /> Select Batches
          </label>

          <div style={{ position: 'relative', flex: 1 }}>
            <div
              onClick={() => setIsBatchDropdownOpen(!isBatchDropdownOpen)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '0.9rem', color: '#1e293b', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.02)', transition: 'all 0.2s', ...(isBatchDropdownOpen ? { borderColor: '#8b5cf6', boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)' } : {}) }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: selectedBatches.length > 0 ? 600 : 400 }}>
                {selectedBatches.length === 0 ? 'All Batches' : `${selectedBatches.length} Batch${selectedBatches.length > 1 ? 'es' : ''} Selected`}
              </span>
              <ChevronDown size={16} color="#94a3b8" style={{ transform: isBatchDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
            </div>

            {isBatchDropdownOpen && (
              <div style={{ position: 'absolute', top: '100%', left: 0, minWidth: '280px', marginTop: '8px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', padding: '8px 0' }}>
                <div
                  role="button"
                  aria-pressed={selectedBatches.length === 0}
                  onClick={selectAllBatches}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectAllBatches();
                    }
                  }}
                  tabIndex={0}
                  style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'background 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <input type="checkbox" checked={selectedBatches.length === 0} readOnly style={{ margin: 0, marginRight: '12px', cursor: 'pointer', flex: '0 0 auto', width: '16px', height: '16px', accentColor: '#8b5cf6' }} />
                  <span style={{ fontSize: '0.9rem', color: '#334155', fontWeight: selectedBatches.length === 0 ? 600 : 400, flex: 1, textAlign: 'left', lineHeight: '1.4' }}>All Batches</span>
                </div>
                <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }}></div>
                {availableBatches.map(b => (
                  <div
                    key={b}
                    onClick={() => {
                      if (selectedBatches.includes(b)) setSelectedBatches(selectedBatches.filter(item => item !== b));
                      else setSelectedBatches([...selectedBatches, b]);
                    }}
                    style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'background 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input type="checkbox" checked={selectedBatches.includes(b)} readOnly style={{ margin: 0, marginRight: '12px', cursor: 'pointer', flex: '0 0 auto', width: '16px', height: '16px', accentColor: '#8b5cf6' }} />
                    <span style={{ fontSize: '0.9rem', color: '#334155', fontWeight: 400, flex: 1, textAlign: 'left', lineHeight: '1.4' }} title={b}>{b}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: '2', minWidth: '450px', position: 'relative', zIndex: isDropdownOpen ? 30 : 'auto', display: 'flex', alignItems: 'center', gap: '12px' }} ref={dropdownRef}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
            <Briefcase size={15} color="#64748b" /> Select Job Role
          </label>
          <div style={{ position: 'relative', flex: 1 }}>
            <div
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '0.9rem', color: '#1e293b', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.02)', transition: 'all 0.2s', ...(isDropdownOpen ? { borderColor: '#0ea5e9', boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)' } : {}) }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: selectedJobs.length > 0 ? 600 : 400 }}>
                {selectedJobs.length === 0 ? '-- General Market Demand --' : `${selectedJobs.length} Role${selectedJobs.length > 1 ? 's' : ''} Selected`}
              </span>
              <ChevronDown size={16} color="#94a3b8" style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
            </div>

            {isDropdownOpen && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '8px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: '300px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc' }}>
                  <Search size={18} color="#64748b" />
                  <input
                    type="text"
                    placeholder="Type to search..."
                    value={jobSearchText}
                    onChange={(e) => setJobSearchText(e.target.value)}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '0.95rem', color: '#1e293b', background: 'transparent' }}
                    autoFocus
                  />
                </div>
                <div style={{ overflowY: 'auto', flex: '1', padding: '8px 0' }}>
                  <div
                    role="button"
                    aria-pressed={selectedJobs.length === 0}
                    onClick={() => { setSelectedJobs([]); setIsDropdownOpen(false); }}
                    style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'background 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input type="checkbox" checked={selectedJobs.length === 0} readOnly style={{ margin: 0, marginRight: '12px', cursor: 'pointer', flex: '0 0 auto', width: '16px', height: '16px', accentColor: '#0ea5e9' }} />
                    <span style={{ fontSize: '0.9rem', color: '#334155', fontWeight: selectedJobs.length === 0 ? 600 : 400, flex: 1, textAlign: 'left', lineHeight: '1.4' }}>-- General Market Demand --</span>
                  </div>
                  {sortedJobs
                    .filter(j => `${j.job_name} ${j.company_name || ''}`.toLowerCase().includes(jobSearchText.toLowerCase()))
                    .map(j => (
                      <div
                        key={j.selectionKey}
                        onClick={() => {
                          if (selectedJobs.includes(j.selectionKey)) setSelectedJobs(selectedJobs.filter(key => key !== j.selectionKey));
                          else setSelectedJobs([...selectedJobs, j.selectionKey]);
                        }}
                        style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <input type="checkbox" checked={selectedJobs.includes(j.selectionKey)} readOnly style={{ margin: 0, marginRight: '12px', cursor: 'pointer', flex: '0 0 auto', width: '16px', height: '16px', accentColor: '#0ea5e9' }} />
                        <span style={{ fontSize: '0.9rem', color: '#334155', fontWeight: 400, flex: 1, textAlign: 'left', lineHeight: '1.4' }}>
                          {j.job_name} {j.company_name ? <span style={{ color: '#64748b' }}>({j.company_name})</span> : ''}
                          {j.roleSource === 'campus' && (
                            <span style={{ marginLeft: '8px', fontSize: '0.7rem', fontWeight: 700, color: '#0369a1', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: '4px', padding: '1px 6px', whiteSpace: 'nowrap' }}>
                              Campus Job
                            </span>
                          )}
                        </span>
                      </div>
                    ))
                  }
                  {sortedJobs.filter(j => `${j.job_name} ${j.company_name || ''}`.toLowerCase().includes(jobSearchText.toLowerCase())).length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.95rem' }}>No matching job roles found.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <button 
          onClick={fetchMarketRequirements} 
          disabled={analyzingJD}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#0ea5e9', color: '#fff', fontSize: '0.9rem', fontWeight: 600, cursor: analyzingJD ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap', opacity: analyzingJD ? 0.7 : 1 }}
        >
          <Activity size={18} />
          {analyzingJD ? 'Analyzing...' : 'Analyze with AI'}
        </button>
      </div>

      {/* Main Content Area */}
      {analyzingJD ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', background: 'linear-gradient(145deg, #f8fafc, #f1f5f9)', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
          <Activity size={48} color="#0ea5e9" style={{ marginBottom: '20px', animation: 'pulse 2s infinite' }} />
          <h3 style={{ color: '#1e293b', margin: '0 0 10px', fontSize: '1.25rem' }}>AI Agent Analyzing Skills...</h3>
          <p style={{ color: '#64748b', fontSize: '1rem', maxWidth: '400px', margin: '0 auto' }}>Aggregating and evaluating the most critical market requirements across your selected job roles.</p>
        </div>
      ) : analysisError ? (
        <div style={{ textAlign: 'center', padding: '60px', background: 'linear-gradient(145deg, #fef2f2, #fee2e2)', borderRadius: '16px', border: '1px solid #fecaca' }}>
          <AlertTriangle size={40} color="#ef4444" style={{ marginBottom: '16px' }} />
          <p style={{ color: '#991b1b', fontWeight: 600, fontSize: '1.1rem' }}>{analysisError}</p>
        </div>
      ) : chartData.length > 0 && insights ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '24px' }}>

          {/* Radar Chart */}
          <div style={{ background: 'linear-gradient(145deg, #ffffff, #fcfcfd)', borderRadius: '20px', padding: '28px', border: '1px solid rgba(226, 232, 240, 0.6)', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '1.15rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0ea5e9', boxShadow: '0 0 10px rgba(14, 165, 233, 0.5)' }}></div>
              Skill Proficiency Matrix
            </h3>
            <div style={{ width: '100%', height: '350px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
                  <PolarGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                  <PolarAngleAxis dataKey="subject" tick={<CustomAxisTick />} />
                  <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 10 }} tickCount={6} axisLine={false} />
                  <Radar name="Student Average" dataKey="StudentAverage" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.4} strokeWidth={2.5} dot={{ r: 4, fill: '#0ea5e9', strokeWidth: 2, stroke: '#fff' }} />
                  <Radar name="Market Requirement" dataKey="MarketRequirement" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4, fill: '#ef4444', strokeWidth: 2, stroke: '#fff' }} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '20px', fontWeight: 500 }} iconType="circle" />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Readiness Distribution */}
          <div style={{ background: 'linear-gradient(145deg, #ffffff, #fcfcfd)', borderRadius: '20px', padding: '28px', border: '1px solid rgba(226, 232, 240, 0.6)', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '1.15rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#8b5cf6', boxShadow: '0 0 10px rgba(139, 92, 246, 0.5)' }}></div>
              Batch Readiness Distribution
            </h3>

            <div style={{ flex: '1', minHeight: '180px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={studentReadiness.distribution} margin={{ top: 20, right: 30, left: -20, bottom: 5 }} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: '#334155', fontWeight: 600 }} width={140} axisLine={false} tickLine={false} />
                  <RechartsTooltip cursor={{ fill: 'rgba(248, 250, 252, 0.6)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', padding: '12px 16px' }} />
                  <Bar dataKey="count" radius={[0, 8, 8, 0]} maxBarSize={32}>
                    {studentReadiness.distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top Candidates Leaderboard */}
            <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 16px', color: '#334155', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Award size={18} color="#f59e0b" /> Top Candidates
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {studentReadiness.topCandidates.length > 0 ? (
                  studentReadiness.topCandidates.map((cand, idx) => (
                    <div key={cand.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: idx === 0 ? '#fef3c7' : '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: idx === 0 ? '#d97706' : '#0369a1', fontSize: '0.8rem', fontWeight: 700 }}>
                          #{idx + 1}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{cand.name}</p>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>{cand.branch}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: 700, color: cand.score >= 80 ? '#16a34a' : cand.score >= 50 ? '#d97706' : '#dc2626' }}>{cand.score}%</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>No candidates available.</div>
                )}
              </div>
            </div>
          </div>

          {/* Key Insights & Gaps Matrix */}
          <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>

            {/* Skill Alignment Matrix */}
            <div style={{ background: 'linear-gradient(145deg, #ffffff, #fcfcfd)', borderRadius: '20px', padding: '20px', border: '1px solid rgba(226, 232, 240, 0.6)', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ec4899', boxShadow: '0 0 10px rgba(236, 72, 153, 0.5)' }}></div>
                Skill Alignment Matrix
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Strong Areas */}
                <div>
                  <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#15803d', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Star size={14} /> Strong Proficiencies
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {chartData.filter(item => item.StudentAverage >= item.MarketRequirement - 1).length === 0 && (
                      <span style={{ fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic' }}>No significant strengths identified.</span>
                    )}
                    {chartData.filter(item => item.StudentAverage >= item.MarketRequirement - 1).map((item, i) => {
                      const percentage = Math.min((item.StudentAverage / item.MarketRequirement) * 100, 100) || 0;
                      return (
                        <div key={`strong-${i}`}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{item.subject}</span>
                            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#16a34a' }}>
                              {item.StudentAverage} <span style={{ color: '#94a3b8', fontWeight: 400 }}>/ {item.MarketRequirement}</span>
                            </span>
                          </div>
                          <div style={{ height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: `${percentage}%`, height: '100%', background: '#10b981', borderRadius: '3px', transition: 'width 1s ease-out' }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ height: '1px', background: '#e2e8f0', margin: '2px 0' }}></div>

                {/* Gaps */}
                <div>
                  <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <AlertTriangle size={14} /> Critical Gaps
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {chartData.filter(item => item.StudentAverage < item.MarketRequirement - 1).length === 0 && (
                      <span style={{ fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic' }}>No critical gaps identified.</span>
                    )}
                    {chartData.filter(item => item.StudentAverage < item.MarketRequirement - 1).map((item, i) => {
                      const percentage = Math.min((item.StudentAverage / item.MarketRequirement) * 100, 100) || 0;
                      const isGap = item.MarketRequirement - item.StudentAverage > 2;
                      return (
                        <div key={`gap-${i}`}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{item.subject}</span>
                            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: isGap ? '#dc2626' : '#d97706' }}>
                              {item.StudentAverage} <span style={{ color: '#94a3b8', fontWeight: 400 }}>/ {item.MarketRequirement}</span>
                            </span>
                          </div>
                          <div style={{ height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: `${percentage}%`, height: '100%', background: isGap ? '#ef4444' : '#f59e0b', borderRadius: '3px', transition: 'width 1s ease-out' }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Recommended Actions */}
            <div style={{ background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)', border: '1px solid #bae6fd', borderRadius: '20px', padding: '28px' }}>
              <h4 style={{ margin: '0 0 20px', color: '#0369a1', fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' }}>
                <TrendingUp size={22} color="#0284c7" /> Strategic Recommendations
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {insights.recommendations.map((rec, i) => (
                  <div key={i} style={{ display: 'flex', gap: '12px', background: 'rgba(255,255,255,0.6)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.8)' }}>
                    <div style={{ color: '#0ea5e9', flexShrink: 0, marginTop: '2px' }}><CheckCircle size={18} /></div>
                    <span style={{ color: '#0c4a6e', fontSize: '0.9rem', lineHeight: 1.5, fontWeight: 500 }}>{rec}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px', background: 'linear-gradient(145deg, #f8fafc, #f1f5f9)', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
          <Activity size={40} color="#94a3b8" style={{ marginBottom: '16px' }} />
          <h3 style={{ color: '#64748b', margin: '0 0 8px', fontSize: '1.1rem' }}>No Data Available</h3>
          <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>Select a job role and batch with sufficient data to view analytics.</p>
        </div>
      )}
    </div>
  );
};

export default BatchSkillAnalytics;
